import { type FunctionComponent, useRef } from 'react';
import { ViewIds } from '@ardoq/api-types';
import { connect } from '@ardoq/rxbeach';
import { toViewpointModeMainViewInstanceId } from 'appLayout/mainViewInstanceIds';
import { getViewpointModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/viewpointModeViewSettings$';
import { HierarchicalTreemapVisualization } from '../view/standard/HierarchicalTreemapVisualization';
import { HierarchicalViewpointShell } from './HierarchicalViewpointShell';
import { getViewpointViewModel$ } from './viewpointViewModel$';
import type { HierarchicalTreemapViewpointViewProperties } from './types';

const VIEW_ID = ViewIds.HIERARCHICAL_TREEMAP;
const VIEW_INSTANCE_ID = toViewpointModeMainViewInstanceId(VIEW_ID);

/**
 * The treemap in viewpoint mode.
 *
 * Everything the workspace-mode view does about being in a mode is gone: no settings bar to build
 * and hand to the shell, no `isViewpointMode` for the shell to branch on, and a settings object
 * with two fields rather than nine. What is left is a hierarchy, the two numbers the layout needs,
 * and the shell that publishes the view's controls to the panel that owns them.
 */
const HierarchicalTreemapViewpointView = (
  props: HierarchicalTreemapViewpointViewProperties
) => {
  const {
    sizedNodes,
    viewSettings: { tilingMethod, maxDepth },
    formatValue,
  } = props;
  const viewContainer = useRef<HTMLDivElement>(null);

  return (
    <HierarchicalViewpointShell
      viewId={ViewIds.HIERARCHICAL_TREEMAP}
      properties={props}
      viewContainer={viewContainer}
    >
      <HierarchicalTreemapVisualization
        sizedNodes={sizedNodes}
        tilingMethod={tilingMethod}
        maxDepth={maxDepth}
        formatValue={formatValue}
        viewContainer={viewContainer}
        i18nLocale={props.i18nLocale}
      />
    </HierarchicalViewpointShell>
  );
};

const ConnectedHierarchicalTreemapViewpointView: FunctionComponent = connect(
  HierarchicalTreemapViewpointView,
  getViewpointViewModel$({
    viewId: VIEW_ID,
    viewInstanceId: VIEW_INSTANCE_ID,
    viewSettingsWithChanges$: getViewpointModeViewSettingsWithChanges$(
      VIEW_ID,
      VIEW_INSTANCE_ID
    ),
  })
);

export {
  HierarchicalTreemapViewpointView,
  ConnectedHierarchicalTreemapViewpointView,
};
